// ── Covenant tab — covenants + exceptions for an account ──
import { Fragment, useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Badge, Btn, Card, Field, Icon, PanelTitle, Spinner, StatCard, STh, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { ExceptionForm } from "./ExceptionForm";
import { ExceptionsTable } from "./ExceptionsTable";

export const CovenantTab = ({ accountid, accountFundingDate, accountMaturityDate, canEdit, canDelete }) => {
    const { api, delRec, setConfirm, setModal, view } = useApp();
      const [covenants, setCovenants] = useState([]);
      const [calendar, setCalendar]   = useState([]);
      const [loading, setLoading]     = useState(true);
      const [fieldExamExceptions, setFieldExamExceptions] = useState([]);   // NEW
      const [excEntry, setExcEntry] = useState(null);                       // NEW: open exception
      const load = () => {
        setLoading(true);
        Promise.all([ api.get("getCovenants?accountid="+accountid), api.get("getCovenantCalendar?accountid="+accountid) ])
          .then(([cv,cal])=>{ setCovenants(cv); setCalendar(cal); setLoading(false); }).catch(()=>setLoading(false));
      };
      useEffect(()=>{ load(); }, [accountid]);
 
      // NEW: exceptions from the most recent completed Field Exam calendar entry
      const loadFieldExamExceptions = () => {
        const recent = [...calendar]
          .filter(e => e.covenantname === "Field Exam" && e.status === "Completed" && e.completeddate)
          .sort((a,b) => (b.completeddate||"").localeCompare(a.completeddate||""))[0];
        if (recent) api.get("getCovenantExceptions?covenantcalendarid="+recent.id).then(setFieldExamExceptions).catch(()=>setFieldExamExceptions([]));
        else setFieldExamExceptions([]);
      };
      useEffect(loadFieldExamExceptions, [calendar]);

      // ── Sorting ────────────────────────────────────────────────────────────
      //
      // BOTH useSortable CALLS LIVE ABOVE THE `if (loading)` RETURN, and must.
      // Hooks have to run in the same order on every render; putting one after
      // an early return means the first render (loading) calls two hooks and
      // the second calls four, which React treats as a bug in this component
      // rather than a bug in the render it produced.
      //
      // The covenants table starts UNSORTED — defaultKey '' makes the
      // comparator return 0 for every pair, and Array.prototype.sort is stable,
      // so the server's order survives until somebody clicks a header.
      const cvSort = useSortable(covenants, '', 'asc');

      // The calendar starts on test date ascending, which is exactly what the
      // old hard-coded `.sort((a,b)=>(a.testdate||"")...)` did — so the first
      // render of this tab looks the same as it always has.
      const calSort = useSortable(calendar, 'testdate', 'asc');

      if (loading) return <Spinner />;

      const hasFieldExam = covenants.some(c => c.covenantname === "Field Exam");
      const today = new Date().toISOString().slice(0,10);
      const open = calendar.filter(e=>!["Completed","Canceled","Waived"].includes(e.status));
      const overdue = open.filter(e=>e.duedate && e.duedate.slice(0,10) < today).length;
      const next = open.filter(e=>e.duedate && e.duedate.slice(0,10) >= today).sort((a,b)=>a.duedate.localeCompare(b.duedate))[0];
      // SORT FIRST, THEN BUCKET. The rows are pulled out of the already-sorted
      // array, so each group comes out in the chosen order without this file
      // owning a second copy of the comparator — the one in useSortable stays
      // the only place that knows a date is not a number and a blank sorts last.
      //
      // The GROUPS themselves stay alphabetical whatever the sort. Reordering
      // them too would mean the header you were reading moves out from under
      // you every time you change column, and the point of the grouping is that
      // a covenant's entries are always findable in the same place.
      const groups = [...new Set(calendar.map(e=>e.covenantname||"—"))].sort((a,b)=>a.localeCompare(b))
        .map(name=>({ name, rows: calSort.sorted.filter(e=>(e.covenantname||"—")===name) }));

      // Three cards — Covenants, Field Exam Exceptions, Covenant Calendar — each
      // titled INSIDE the card (PanelTitle), like the Overview panels.
      return <>
        <Card style={{ overflowX:"auto", marginBottom:16 }}>
        <PanelTitle title="Covenants" count={covenants.length} actions={
          canEdit&&<Btn variant="primary" onClick={()=>setModal({ type:"covenant-add", accountid, accountFundingDate, accountMaturityDate, onDone:load })}>+ Add Covenant</Btn>} />
 
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:16 }}>
          <StatCard label="Covenants" value={covenants.length} />
          <StatCard label="Calendar Entries" value={calendar.length} />
          <StatCard label="Open" value={open.length} />
          <StatCard label="Overdue" value={overdue} />
          <StatCard label="Next Due" value={next?fmtDate(next.duedate):"—"} sub={next?next.covenantname:null} />
        </div>
 
        {covenants.length===0 && <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"4px 0" }}>No covenants on this account yet.</div>}
        {covenants.length>0 &&
          <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <STh label="Covenant"    sortKey="covenantname"      currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} />
              <STh label="Category"    sortKey="covenantcategory"  currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} />
              <STh label="Frequency"   sortKey="covenantfrequency" currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} />
              <STh label="Responsible" sortKey="responsibleparty"  currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} />
              <STh label="Direction"   sortKey="covenantdirection" currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} />
              <STh label="First Test"  sortKey="firsttestdate"     currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} />
              <STh label="Entries"     sortKey="calendarcount"     currentKey={cvSort.sortKey} currentDir={cvSort.sortDir} onSort={cvSort.toggle} numeric />
              {/* The actions column has nothing to sort by. */}
              <Th></Th>
            </tr></thead>
            <tbody>{cvSort.sorted.map(c=>(
              <tr key={c.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"covenant-view",rec:c,accountid,accountFundingDate,accountMaturityDate,onDone:load})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                <Td style={{ fontWeight:500 }}>{c.covenantname||"—"}</Td>
                <Td>{c.covenantcategory||"—"}</Td>
                <Td>{c.covenantfrequency||"—"}</Td>
                <Td>{c.responsibleparty||"—"}</Td>
                <Td>{c.covenantdirection||"—"}</Td>
                <Td>{fmtDate(c.firsttestdate)}</Td>
                <TdNum>{c.calendarcount||0}</TdNum>
                <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                  <Btn onClick={()=>setModal({type:"covenant-view",rec:c,accountid,accountFundingDate,accountMaturityDate})}><Icon type="view" /></Btn>
                  {canEdit&&<Btn onClick={()=>setModal({type:"covenant-edit",rec:c,accountid,accountFundingDate,accountMaturityDate,onDone:load})}><Icon type="edit" /></Btn>}
                  {canDelete&&<Btn variant="danger" onClick={()=>setConfirm({msg:"Delete covenant "+(c.covenantname||"")+"? Calendar entries remain unless deleted separately.",onConfirm:()=>delRec("Covenant",c.id,"Deleted covenant",load)})}><Icon type="delete" /></Btn>}
                </div></Td>
              </tr>
            ))}</tbody>
          </table>}
        </Card>
 
        {/* ===== Field Exam Exceptions (between Covenant table and Covenant Calendar) ===== */}
        {hasFieldExam && <Card style={{ marginBottom:16 }}>
          <PanelTitle title="Field Exam Exceptions" count={(fieldExamExceptions || []).length} />
          <ExceptionsTable rows={fieldExamExceptions} onRowClick={setExcEntry} bare />
        </Card>}
 
        {excEntry && <ExceptionForm initial={excEntry} onClose={()=>setExcEntry(null)} onSaved={loadFieldExamExceptions} />}
 
        <Card style={{ overflowX:"auto" }}>
        <PanelTitle title="Covenant Calendar" count={calendar.length} />
        {calendar.length===0
          ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"4px 0" }}>No calendar entries. Open a covenant and click Generate Calendar.</div>
          : <>
              <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                <thead><tr>
                  <STh label="Status"         sortKey="status"              currentKey={calSort.sortKey} currentDir={calSort.sortDir} onSort={calSort.toggle} />
                  <STh label="Test Date"      sortKey="testdate"            currentKey={calSort.sortKey} currentDir={calSort.sortDir} onSort={calSort.toggle} />
                  <STh label="Due Date"       sortKey="duedate"             currentKey={calSort.sortKey} currentDir={calSort.sortDir} onSort={calSort.toggle} />
                  <STh label="Completed"      sortKey="completeddate"       currentKey={calSort.sortKey} currentDir={calSort.sortDir} onSort={calSort.toggle} />
                  <STh label="PM"             sortKey="portfoliomanagername" currentKey={calSort.sortKey} currentDir={calSort.sortDir} onSort={calSort.toggle} />
                  {/* Direction is NOT sortable here, unlike in the Covenants
                      table above. Every row in a group belongs to the same
                      covenant, so they all carry the same direction — a header
                      that reorders nothing is worse than a plain one, because
                      it invites a click and then looks broken. */}
                  <Th>Direction</Th>
                  <STh label="Reported Value" sortKey="covenantvalue"       currentKey={calSort.sortKey} currentDir={calSort.sortDir} onSort={calSort.toggle} />
                  {/* Box is a link, present or absent — sorting it would rank
                      "open" against "—", which tells nobody anything. */}
                  <Th>Box</Th>
                  <Th></Th>
                </tr></thead>
                <tbody>
                  {/* Fragment with a key, not <>. The shorthand cannot take one,
                      and React was warning about a missing key on every render
                      of this table. */}
                  {groups.map(g=>(<Fragment key={g.name}>
                    <tr>
                      <td colSpan={9} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                        {g.name}<span style={{ marginLeft:8, fontWeight:400 }}>{g.rows.length} entr{g.rows.length!==1?"ies":"y"}</span>
                      </td>
                    </tr>
                    {g.rows.map(e=>(
                      <tr key={e.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"covenantcalendar-view",rec:e})} onMouseEnter={ev=>ev.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={ev=>ev.currentTarget.style.background=""}>
                        <Td><Badge s={e.status||"—"} /></Td>
                        <Td>{fmtDate(e.testdate)}</Td>
                        <Td>{fmtDate(e.duedate)}</Td>
                        <Td>{e.completeddate?fmtDate(e.completeddate):"—"}</Td>
                        <Td>{e.portfoliomanagername||"—"}</Td>
                        <Td>{e.covenantdirection||"—"}</Td>
                        <Td>{e.covenantvalue||"—"}</Td>
                        <Td>{e.boxurl?<a href={e.boxurl} target="_blank" rel="noreferrer" onClick={ev=>ev.stopPropagation()} style={{ color:B.cyan }}>open</a>:"—"}</Td>
                        <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={ev=>ev.stopPropagation()}>
                          <Btn onClick={()=>setModal({type:"covenantcalendar-view",rec:e})}><Icon type="view" /></Btn>
                          {canEdit&&<Btn onClick={()=>setModal({type:"covenantcalendar-edit",rec:e,onDone:load})}><Icon type="edit" /></Btn>}
                          {canDelete&&<Btn variant="danger" onClick={()=>setConfirm({msg:"Delete this calendar entry?",onConfirm:()=>delRec("CovenantCalendar",e.id,"Deleted covenant calendar entry",load)})}><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    ))}
                  </Fragment>))}
                </tbody>
              </table>
            </>}
        </Card>
      </>;
    };
